/**
 * Data shaping for the Insights cycle chart: one row per cycle day with every recorded sign,
 * the phase ranges behind it, and the multi-cycle temperature overlay.
 *
 * Display only. Peak Day, the fertile window, the thermal shift and the LH surge all come from
 * the shared rules in @nfp-tracker/utils (computeFirstPeakDay via derivedCycles, getCyclePhases,
 * detectThermalShift, findLhSurgeDay) — nothing here decides fertility, it only lines those
 * results up by cycle day.
 */

import { addDays, differenceInCalendarDays, format, parseISO } from "date-fns";
import {
  BLEEDING_INTENSITY_RANK,
  BLEEDING_OPTIONS,
  MUCUS_COLOR_OPTIONS,
  MUCUS_CONSISTENCY_OPTIONS,
  detectThermalShift,
  formatTemperature,
  getCycleLength,
  getCyclePhases,
  getCycleTiming,
  getTodayKey,
  isCycleOngoing,
  isPastCycle,
  getPhasesForCycleDay,
  isPeakMucus,
  isPostPeakNonFertileDay,
  normalizeMonitor,
  normalizeOvulationTest,
  usesMonitor,
  usesSymptoThermal,
  type CyclePhaseKey,
  type FertileWindow,
  type MonitorStatus,
  type NFPMethod,
  type OvulationTestResult,
  isPostPeakPhaseDay,
  type PhaseSpan,
} from "@nfp-tracker/utils";

export interface ChartCycle {
  id: string;
  start_date: string;
  end_date: string | null;
  first_peak_cd: number | null;
  /**
   * Partial cycle: logged before the first day marked "cycle start", so its real CD 1 is
   * unknown and its day numbers are only offsets from the first logged day (derivedCycles).
   */
  start_inferred?: boolean;
}

/** Cycles whose CD 1 was actually recorded (mirrors isRecordedCycle in derivedCycles). */
export const isRecordedChartCycle = (cycle: Pick<ChartCycle, "start_inferred">) =>
  !cycle.start_inferred;

export interface ChartObservation {
  cycle_id: string;
  cycle_day: number;
  date: string;
  temperature_f: number | null;
  monitor: string | null;
  ovulation_test: string | null;
  mucus: string | null;
  mucus_consistency: string | null;
  mucus_color: string | null;
  bleeding: number | null;
  intercourse: boolean | null;
  symptoms: string[] | null;
  notes: string | null;
}

/** Lanes under the temperature line, top to bottom. */
export type LaneKey = "monitor" | "opk" | "mucus" | "bleeding" | "intercourse" | "symptoms";

export const LANE_ORDER: LaneKey[] = [
  "monitor",
  "opk",
  "mucus",
  "bleeding",
  "intercourse",
  "symptoms",
];

export const LANE_LABELS: Record<LaneKey, string> = {
  monitor: "Monitor",
  opk: "OPK",
  mucus: "Mucus",
  bleeding: "Bleeding",
  intercourse: "Intercourse",
  symptoms: "Symptoms",
};

/**
 * - peak: peak-type mucus (isPeakMucus) inside the fertile taper
 * - post-peak: peak-type mucus more than 3 days past Peak Day — not read as fertile
 * - mucus: any other mucus
 * - dry: explicitly recorded dry / none
 */
export type MucusKind = "peak" | "post-peak" | "mucus" | "dry";

export interface ChartBleeding {
  code: number;
  label: string;
  /** Relative intensity for display (BLEEDING_INTENSITY_RANK), 0.5–3. */
  rank: number;
}

export interface CycleChartDay {
  cycleDay: number;
  /** yyyy-MM-dd */
  date: string;
  hasObservation: boolean;
  temperature: number | null;
  monitor: MonitorStatus | null;
  opk: OvulationTestResult | null;
  mucus: { kind: MucusKind; label: string } | null;
  bleeding: ChartBleeding | null;
  intercourse: boolean;
  symptoms: string[];
  notes: string | null;
  phases: CyclePhaseKey[];
}

export interface CoverLine {
  value: number;
  /**
   * thermal-shift: the 6 temperatures before the detected shift + 0.4°F, i.e. the exact
   * threshold detectThermalShift tested against. provisional: no shift yet, so the first six
   * temperatures of the cycle + 0.4°F (what the Insights chart has always shown).
   */
  basis: "thermal-shift" | "provisional";
}

export interface CycleChartModel {
  cycleId: string;
  startDate: string;
  endDate: string | null;
  /** Still running as of today (no end yet, or an end that's still ahead). */
  isOngoing: boolean;
  /** Starts after today: pre-logged data, shown as recorded but not happened yet. */
  isUpcoming: boolean;
  /** Today's cycle day when today falls inside this cycle, else null. */
  todayCd: number | null;
  /** Start not recorded: day numbers are offsets, so the UI labels days by date instead. */
  partial: boolean;
  /** Number of day columns. */
  length: number;
  days: CycleChartDay[];
  spans: PhaseSpan[];
  window: FertileWindow;
  peakCd: number | null;
  lhSurgeCd: number | null;
  thermalShiftCd: number | null;
  coverLine: CoverLine | null;
  lanes: LaneKey[];
  /** Temperatures exist in this cycle. */
  hasTemperatures: boolean;
  /** Temperature is on in the user's optional fields (so an empty panel is meaningful). */
  temperatureEnabled: boolean;
  temperatureDomain: [number, number];
}

export type OptionalFields = Record<string, boolean> | null | undefined;

export interface HistoryOptions {
  methods: NFPMethod[];
  isPostpartum?: boolean;
  /** Shortest completed cycle, for the shortest − 20 start rule (see getCyclePhases). */
  shortestCycleLength?: number | null;
  completedCycleCount?: number;
  optionalFields?: OptionalFields;
  /** Today's date key (owner's timezone); defaults to the device's today. */
  today?: string;
}

const DRY_VALUES = new Set(["dry", "none"]);
const COVER_LINE_OFFSET_F = 0.4;
const DEFAULT_TEMPERATURE_DOMAIN: [number, number] = [97, 98.6];

/** Same defaults as the Observations entry form: everything on except the OPK field. */
export const isLaneEnabled = (
  lane: LaneKey,
  optionalFields: OptionalFields,
  methods: readonly string[]
): boolean => {
  const fields = optionalFields ?? {};
  switch (lane) {
    case "monitor":
      return usesMonitor(methods) && fields.monitor !== false;
    case "opk":
      return fields.ovulation_test === true;
    case "mucus":
      return fields.mucus !== false;
    case "bleeding":
      return fields.bleeding !== false;
    case "intercourse":
      return fields.intercourse !== false;
    case "symptoms":
      return fields.symptoms !== false;
  }
};

const laneHasData = (lane: LaneKey, day: CycleChartDay): boolean => {
  switch (lane) {
    case "monitor":
      return day.monitor !== null;
    case "opk":
      return day.opk !== null;
    case "mucus":
      return day.mucus !== null;
    case "bleeding":
      return day.bleeding !== null;
    case "intercourse":
      return day.intercourse;
    case "symptoms":
      return day.symptoms.length > 0;
  }
};

/**
 * A lane appears only when its field is switched on AND this cycle has something in it. Empty
 * lanes were just noise, and a field the user turned off stays off even if older entries exist.
 */
export const resolveVisibleLanes = (
  days: CycleChartDay[],
  optionalFields: OptionalFields,
  methods: readonly string[]
): LaneKey[] =>
  LANE_ORDER.filter(
    (lane) =>
      isLaneEnabled(lane, optionalFields, methods) && days.some((day) => laneHasData(lane, day))
  );

const labelFrom = (
  options: readonly { value: string; label: string }[],
  value: string | null | undefined
): string | null => {
  if (!value) return null;
  return options.find((option) => option.value === value)?.label ?? value;
};

export const describeMucus = (
  obs: Pick<ChartObservation, "mucus" | "mucus_consistency" | "mucus_color">,
  cycleDay: number,
  peakCd: number | null,
  /** Phase spans for the cycle; when given, "post-peak" follows the computed post-Peak phase
   * (which can run past Peak + 3 for Sympto-Thermal / returning mucus) instead of Peak + 3. */
  spans?: readonly PhaseSpan[]
): { kind: MucusKind; label: string } | null => {
  const consistency = (obs.mucus_consistency || "").toLowerCase();
  const legacy = (obs.mucus || "").toLowerCase();
  const color = (obs.mucus_color || "").toLowerCase();
  const value = consistency || legacy;
  if (!value && !color) return null;

  const parts = [
    labelFrom(MUCUS_CONSISTENCY_OPTIONS, obs.mucus_consistency) ??
      (obs.mucus_consistency ? null : obs.mucus),
    labelFrom(MUCUS_COLOR_OPTIONS, obs.mucus_color),
  ].filter(Boolean) as string[];
  const label = parts.join(", ") || value;

  if (DRY_VALUES.has(value) && (!color || color === "none")) {
    return { kind: "dry", label };
  }
  if (isPeakMucus(obs.mucus_consistency, obs.mucus, obs.mucus_color)) {
    return {
      kind: (spans ? isPostPeakPhaseDay(cycleDay, spans) : isPostPeakNonFertileDay(cycleDay, peakCd))
        ? "post-peak"
        : "peak",
      label,
    };
  }
  return { kind: "mucus", label };
};

export const describeBleeding = (code: number | null | undefined): ChartBleeding | null => {
  if (!code) return null;
  const label = BLEEDING_OPTIONS.find((option) => option.value === code)?.label ?? `Code ${code}`;
  return { code, label, rank: BLEEDING_INTENSITY_RANK[code] ?? 1 };
};

const average = (values: number[]) => values.reduce((sum, v) => sum + v, 0) / values.length;

/**
 * Cover line for display. With a shift, mirrors the baseline detectThermalShift uses (the six
 * cycle days before the shift, all six needing a temperature); without one, the provisional
 * first-six-temperatures line the Insights chart already showed.
 */
export const computeCoverLine = (
  observations: { cycle_day: number; temperature_f?: number | null }[],
  thermalShiftCd: number | null
): CoverLine | null => {
  const temps = observations
    .filter((obs) => obs.temperature_f)
    .sort((a, b) => a.cycle_day - b.cycle_day);

  if (thermalShiftCd) {
    const baseline = temps.filter(
      (obs) => obs.cycle_day < thermalShiftCd && obs.cycle_day >= thermalShiftCd - 6
    );
    if (baseline.length >= 6) {
      return {
        value: average(baseline.map((obs) => obs.temperature_f as number)) + COVER_LINE_OFFSET_F,
        basis: "thermal-shift",
      };
    }
  }

  if (temps.length < 6) return null;
  return {
    value:
      average(temps.slice(0, 6).map((obs) => obs.temperature_f as number)) + COVER_LINE_OFFSET_F,
    basis: "provisional",
  };
};

/** Y range with a little headroom, at least 1°F tall, snapped to tenths. */
export const computeTemperatureDomain = (values: number[]): [number, number] => {
  const finite = values.filter((v) => Number.isFinite(v));
  if (!finite.length) return DEFAULT_TEMPERATURE_DOMAIN;
  let lo = Math.floor((Math.min(...finite) - 0.2) * 10) / 10;
  let hi = Math.ceil((Math.max(...finite) + 0.2) * 10) / 10;
  const span = hi - lo;
  if (span < 1) {
    const pad = (1 - span) / 2;
    lo = Math.floor((lo - pad) * 10) / 10;
    hi = Math.ceil((hi + pad) * 10) / 10;
  }
  return [Math.round(lo * 10) / 10, Math.round(hi * 10) / 10];
};

/** Axis ticks: every 0.2°F on a tight range, 0.5°F on a wide one. */
export const computeTemperatureTicks = ([lo, hi]: [number, number]): number[] => {
  const step = hi - lo > 2 ? 0.5 : 0.2;
  const first = Math.ceil(lo / step - 1e-9) * step;
  const ticks: number[] = [];
  for (let v = first; v <= hi + 1e-9; v += step) ticks.push(Math.round(v * 100) / 100);
  return ticks;
};

const toDateKey = (startDate: string, cycleDay: number) =>
  format(addDays(parseISO(startDate), cycleDay - 1), "yyyy-MM-dd");

/**
 * Inputs for the shortest − 20 rule, computed the same way the Calendar page does: only cycles
 * that ended before today count as completed (a pre-logged future cycle hasn't happened).
 */
export const getCycleHistoryStats = (
  cycles: ChartCycle[],
  today: string = getTodayKey()
): { shortestCycleLength: number | null; completedCycleCount: number } => {
  const completed = cycles
    .filter((cycle) => isPastCycle(cycle, today) && isRecordedChartCycle(cycle))
    .map((cycle) => getCycleLength(cycle))
    .filter((length): length is number => length !== null);
  return {
    shortestCycleLength: completed.length ? Math.min(...completed) : null,
    completedCycleCount: completed.length,
  };
};

export const buildCycleChartModel = (
  cycle: ChartCycle,
  cycleObservations: ChartObservation[],
  options: HistoryOptions
): CycleChartModel => {
  const sorted = [...cycleObservations].sort((a, b) => a.cycle_day - b.cycle_day);
  const today = options.today ?? getTodayKey();
  const { spans, window } = getCyclePhases(cycle, sorted, {
    today,
    methods: options.methods,
    isPostpartum: options.isPostpartum,
    shortestCycleLength: options.shortestCycleLength,
    completedCycleCount: options.completedCycleCount,
  });
  const length = getCycleLength(cycle, sorted) ?? 0;
  const peakCd = cycle.first_peak_cd ?? null;
  const byDay = new Map(sorted.map((obs) => [obs.cycle_day, obs]));

  const days: CycleChartDay[] = Array.from({ length }, (_, index) => {
    const cycleDay = index + 1;
    const obs = byDay.get(cycleDay);
    return {
      cycleDay,
      date: obs?.date ?? toDateKey(cycle.start_date, cycleDay),
      hasObservation: Boolean(obs),
      temperature: obs?.temperature_f ?? null,
      monitor: normalizeMonitor(obs?.monitor),
      opk: normalizeOvulationTest(obs?.ovulation_test),
      mucus: obs ? describeMucus(obs, cycleDay, peakCd, spans) : null,
      bleeding: describeBleeding(obs?.bleeding),
      intercourse: Boolean(obs?.intercourse),
      symptoms: obs?.symptoms?.filter(Boolean) ?? [],
      notes: obs?.notes?.trim() || null,
      phases: getPhasesForCycleDay(cycleDay, spans).map((span) => span.key),
    };
  });

  // Same input fertileWindow.ts gives it (every observation, sorted), so the shift shown is
  // the shift the Sympto-Thermal double check used.
  const thermalShiftCd = detectThermalShift(sorted);
  const coverLine = computeCoverLine(sorted, thermalShiftCd);
  const temps = days.flatMap((day) => (day.temperature !== null ? [day.temperature] : []));

  return {
    cycleId: cycle.id,
    startDate: cycle.start_date,
    endDate: cycle.end_date,
    isOngoing: isCycleOngoing(cycle, today),
    isUpcoming: getCycleTiming(cycle, today) === "upcoming",
    todayCd:
      getCycleTiming(cycle, today) === "current"
        ? differenceInCalendarDays(parseISO(today), parseISO(cycle.start_date)) + 1
        : null,
    partial: !isRecordedChartCycle(cycle),
    length,
    days,
    spans,
    window,
    peakCd,
    lhSurgeCd: window.lhSurgeCd,
    thermalShiftCd,
    coverLine,
    lanes: resolveVisibleLanes(days, options.optionalFields, options.methods),
    hasTemperatures: temps.length > 0,
    temperatureEnabled: options.optionalFields?.temperature !== false,
    temperatureDomain: computeTemperatureDomain(
      coverLine ? [...temps, coverLine.value] : temps
    ),
  };
};

// ─── Cycle selector ──────────────────────────────────────────────────────────────────────────

export interface CycleOption {
  id: string;
  label: string;
  detail: string;
}

/** Labels for the cycle picker. `cycles` is newest-first, as derivedCycles returns them. */
export const buildCycleOptions = (
  cycles: ChartCycle[],
  observations: ChartObservation[],
  today: string = getTodayKey(),
  { hasFullHistory = true }: { hasFullHistory?: boolean } = {}
): CycleOption[] =>
  cycles.map((cycle) => {
    const start = parseISO(cycle.start_date);
    const cycleObs = observations.filter((obs) => obs.cycle_id === cycle.id);
    const timing = getCycleTiming(cycle, today);
    const partial = !isRecordedChartCycle(cycle);
    // The running cycle's length so far is today's CD, even when later days are pre-logged.
    const length =
      timing === "current" && !partial
        ? differenceInCalendarDays(parseISO(today), start) + 1
        : getCycleLength(cycle, cycleObs);
    const range = cycle.end_date
      ? `${format(start, "MMM d")} – ${format(parseISO(cycle.end_date), "MMM d, yyyy")}`
      : null;
    const from = `from ${format(start, "MMM d")}`;
    const label = partial
      ? `Partial · ${range ?? from}`
      : timing === "upcoming"
        ? `Upcoming (${from})`
        : timing === "current"
          ? `Current cycle (${from})`
          : range ?? `Cycle ${from}`;
    const days = length ? `${length} day${length === 1 ? "" : "s"}` : null;
    // A partial cycle's Peak CD is only an offset from the first logged day, so name its date.
    const peak = cycle.first_peak_cd
      ? partial
        ? `Peak ${format(addDays(start, cycle.first_peak_cd - 1), "MMM d")}`
        : `Peak CD ${cycle.first_peak_cd}`
      : null;
    const detailParts = [
      partial ? (hasFullHistory ? "start not recorded" : "start not in loaded history") : null,
      timing === "upcoming"
        ? "not started yet"
        : days
          ? `${days}${partial ? " logged" : timing === "current" ? " so far" : ""}`
          : null,
      peak,
    ].filter(Boolean);
    return { id: cycle.id, label, detail: detailParts.join(" · ") };
  });

// ─── Multi-cycle overlay ─────────────────────────────────────────────────────────────────────

/**
 * What the cycles are lined up on: CD 1, each cycle's Peak Day, or each cycle's temperature
 * shift (first of the three high temperatures, from detectThermalShift). Peak and shift are
 * read from the shared rules, never re-derived here.
 */
export type OverlayAlign = "cd1" | "peak" | "shift";

export const OVERLAY_ALIGN_LABELS: Record<OverlayAlign, string> = {
  cd1: "Cycle day",
  peak: "Peak Day",
  shift: "Temp shift",
};

export interface OverlayPoint {
  /** Position on the shared axis: cycle day, or days from the anchor day (anchor = 0). */
  x: number;
  cycleDay: number;
  date: string;
  temperature: number;
}

export type OverlayExcludedReason = "no-peak" | "no-shift" | "start-not-recorded";

export interface OverlaySeries {
  cycleId: string;
  label: string;
  peakCd: number | null;
  thermalShiftCd: number | null;
  /** Cycle day the axis is anchored on for this cycle (1 for CD 1 alignment). */
  anchorCd: number | null;
  length: number;
  points: OverlayPoint[];
  partial: boolean;
  /** Why this cycle can't be placed on the shared axis, if it can't. */
  excludedReason: OverlayExcludedReason | null;
  excluded: boolean;
}

export interface OverlayModel {
  align: OverlayAlign;
  series: OverlaySeries[];
  xMin: number;
  xMax: number;
  temperatureDomain: [number, number];
}

export const OVERLAY_EXCLUDED_TEXT: Record<OverlayExcludedReason, string> = {
  "no-peak": "no Peak Day identified",
  "no-shift": "no temperature shift detected",
  "start-not-recorded": "cycle start not recorded",
};

export const buildOverlaySeries = (
  inputs: { cycle: ChartCycle; observations: ChartObservation[]; label: string }[],
  align: OverlayAlign
): OverlayModel => {
  const series: OverlaySeries[] = inputs.map(({ cycle, observations, label }) => {
    const sorted = [...observations].sort((a, b) => a.cycle_day - b.cycle_day);
    const peakCd = cycle.first_peak_cd ?? null;
    const thermalShiftCd = detectThermalShift(sorted);
    const partial = !isRecordedChartCycle(cycle);
    // A partial cycle has no real CD 1, so it can't claim a place on a CD 1 axis; relative to
    // its own Peak or shift it lines up fine, since both are found by the order of days.
    const anchorCd = align === "cd1" ? (partial ? null : 1) : align === "peak" ? peakCd : thermalShiftCd;
    const excludedReason: OverlayExcludedReason | null = anchorCd
      ? null
      : align === "cd1"
        ? "start-not-recorded"
        : align === "peak"
          ? "no-peak"
          : "no-shift";
    const excluded = excludedReason !== null;
    // CD 1 alignment keeps real cycle days on the axis; the others count from the anchor (0).
    const offset = align === "cd1" ? 0 : (anchorCd ?? 0);
    const points = excluded
      ? []
      : sorted
          .filter((obs) => obs.temperature_f)
          .map((obs) => ({
            x: obs.cycle_day - offset,
            cycleDay: obs.cycle_day,
            date: obs.date,
            temperature: obs.temperature_f as number,
          }));
    return {
      cycleId: cycle.id,
      label,
      peakCd,
      thermalShiftCd,
      anchorCd,
      length: getCycleLength(cycle, sorted) ?? 0,
      points,
      partial,
      excludedReason,
      excluded,
    };
  });

  const placed = series.filter((s) => !s.excluded && s.length > 0);
  let xMin = 1;
  let xMax = 1;
  if (align === "cd1") {
    xMax = Math.max(1, ...placed.map((s) => s.length));
  } else if (placed.length) {
    xMin = Math.min(0, ...placed.map((s) => 1 - (s.anchorCd as number)));
    xMax = Math.max(0, ...placed.map((s) => s.length - (s.anchorCd as number)));
  } else {
    xMin = 0;
    xMax = 0;
  }

  return {
    align,
    series,
    xMin,
    xMax,
    temperatureDomain: computeTemperatureDomain(
      placed.flatMap((s) => s.points.map((p) => p.temperature))
    ),
  };
};

/** Column header text on the shared axis. */
export const formatOverlayX = (x: number, align: OverlayAlign): string => {
  if (align === "cd1") return String(x);
  if (x === 0) return align === "peak" ? "P" : "S";
  return x > 0 ? `+${x}` : `−${Math.abs(x)}`;
};

/** Plain-language position on the shared axis, e.g. "2 days after Peak Day". */
export const describeOverlayX = (x: number, align: OverlayAlign): string => {
  if (align === "cd1") return `Cycle day ${x}`;
  const anchor = align === "peak" ? "Peak Day" : "the temperature shift";
  if (x === 0) return align === "peak" ? "Peak Day" : "Temperature shift day";
  const n = Math.abs(x);
  return `${n} day${n === 1 ? "" : "s"} ${x < 0 ? "before" : "after"} ${anchor}`;
};

// ─── Accessible descriptions ─────────────────────────────────────────────────────────────────

const PHASE_LABELS: Record<CyclePhaseKey, string> = {
  menses: "Menses",
  preOvulatory: "Pre-ovulatory",
  fertile: "Fertile window",
  peak: "Peak Day",
  postPeak: "Post-peak infertile",
};

const MONITOR_LABELS: Record<MonitorStatus, string> = {
  low: "Low",
  high: "High",
  peak: "Peak",
};

export const describePhaseKeys = (phases: CyclePhaseKey[]): string[] =>
  phases.map((key) => PHASE_LABELS[key]);

export const monitorLabel = (monitor: MonitorStatus) => MONITOR_LABELS[monitor];

/** Everything recorded on a day as short phrases — shared by the aria-label and details panel. */
export const describeChartDayParts = (
  day: CycleChartDay,
  model: Pick<CycleChartModel, "thermalShiftCd" | "lhSurgeCd">
): string[] => {
  const parts: string[] = [];
  if (day.temperature !== null) parts.push(`Temperature ${formatTemperature(day.temperature, "°F")}`);
  if (day.monitor) parts.push(`Monitor ${MONITOR_LABELS[day.monitor]}`);
  if (day.opk) parts.push(`OPK ${day.opk === "positive" ? "positive" : "negative"}`);
  if (day.mucus) {
    const suffix =
      day.mucus.kind === "peak"
        ? " (peak-type)"
        : day.mucus.kind === "post-peak"
          ? " (peak-type, past Peak + 3)"
          : "";
    parts.push(`Mucus ${day.mucus.label}${suffix}`);
  }
  if (day.bleeding) parts.push(`Bleeding ${day.bleeding.label}`);
  if (day.intercourse) parts.push("Intercourse");
  if (day.symptoms.length) parts.push(`Symptoms ${day.symptoms.join(", ")}`);
  if (model.thermalShiftCd === day.cycleDay) parts.push("Temperature shift (first of 3 high temps)");
  if (model.lhSurgeCd === day.cycleDay) parts.push("LH surge (first positive OPK)");
  return parts;
};

export const describeChartDay = (
  day: CycleChartDay,
  model: Pick<CycleChartModel, "thermalShiftCd" | "lhSurgeCd" | "partial">
): string => {
  const date = format(parseISO(day.date), "EEE, MMM d");
  const phases = describePhaseKeys(day.phases);
  const parts = describeChartDayParts(day, model);
  const head = model.partial ? date : `Cycle day ${day.cycleDay}, ${date}`;
  const phaseText = phases.length ? ` (${phases.join(", ")})` : "";
  const body = parts.length ? parts.join("; ") : day.hasObservation ? "Nothing recorded" : "No entry";
  return `${head}${phaseText}: ${body}`;
};

export const overlayColumnCount = (overlay: OverlayModel) =>
  Math.max(1, overlay.xMax - overlay.xMin + 1);

export const describeOverlayColumn = (
  overlay: OverlayModel,
  xValue: number,
  hiddenIds: Set<string>
): string => {
  const head = describeOverlayX(xValue, overlay.align);
  const parts = overlay.series
    .filter((series) => !series.excluded && !hiddenIds.has(series.cycleId))
    .map((series) => {
      const point = series.points.find((p) => p.x === xValue);
      const where =
        overlay.align !== "cd1" && point
          ? series.partial
            ? ` (${format(parseISO(point.date), "MMM d")})`
            : ` (CD ${point.cycleDay})`
          : "";
      return `${series.label}: ${point ? formatTemperature(point.temperature, "°F") + where : "no temperature"}`;
    });
  return `${head}. ${parts.join("; ")}`;
};

/**
 * Cover line + temperature shift are drawn by default only for methods that chart temperature
 * as a sign (Sympto-Thermal); for monitor/mucus methods they're opt-in, to keep the chart calm.
 */
export const showThermalMarkersByDefault = (methods: readonly string[]) => usesSymptoThermal(methods);
